import type { FilterParams, Paginated } from '../../types/common';
import type { Course } from '../../types/models';

const now = new Date().toISOString();

const instructors = [
  {
    id: 'inst-1',
    name: 'Aaliyah Carter',
    title: 'Senior Full-Stack Engineer',
    bio: 'Aaliyah has 10+ years building web platforms at scale and mentoring junior developers into production-ready engineers.',
    avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400',
    expertise: ['react', 'nodejs', 'system design'],
    coursesCount: 4,
  },
  {
    id: 'inst-2',
    name: 'Marcus Bennett',
    title: 'Staff Backend Engineer',
    bio: 'Marcus specializes in cloud-native backend systems, API performance, and distributed architecture.',
    avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400',
    expertise: ['go', 'microservices', 'postgresql'],
    coursesCount: 3,
  },
  {
    id: 'inst-3',
    name: 'Nia Thompson',
    title: 'Mobile Engineering Lead',
    bio: 'Nia builds high-quality mobile apps and teaches practical architecture patterns for Swift and Kotlin teams.',
    avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400',
    expertise: ['swift', 'kotlin', 'mobile architecture'],
    coursesCount: 3,
  },
] as const;

export const mockCourses: ReadonlyArray<Course> = [
  {
    id: 'course-js-python-variables',
    title: 'Variables and Control Flow: JavaScript + Python',
    description: 'Build core programming fundamentals with variables, conditions, and loops across JavaScript and Python.',
    shortDescription: 'Master variables, conditionals, and loops in JS and Python.',
    thumbnail: 'https://images.unsplash.com/photo-1515879218367-8466d910aaa4?w=1200',
    trailerUrl: 'https://mpb-site.nyc3.digitaloceanspaces.com/vids/homeCourses/Master+Polyglots+-+JS+%26+Python+Intro.mp4',
    instructor: instructors[0],
    level: 'beginner',
    language: 'javascript',
    topics: ['variables', 'conditionals', 'loops', 'problem solving'],
    duration: 720,
    lessonsCount: 18,
    studentsCount: 1450,
    rating: 4.8,
    reviewsCount: 312,
    isPublished: true,
    createdAt: now,
    updatedAt: now,
  },
  {
    id: 'course-java-go-backend',
    title: 'Backend Foundations with Java and Go',
    description: 'Design scalable APIs and services using Java and Go while learning production-grade backend patterns.',
    shortDescription: 'Build APIs and backend services with Java and Go.',
    thumbnail: 'https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=1200',
    trailerUrl: 'https://mpb-site.nyc3.digitaloceanspaces.com/vids/homeCourses/Master+Polyglots+-+Java+%26+Go+Intro.mp4',
    instructor: instructors[1],
    level: 'intermediate',
    language: 'java',
    topics: ['api design', 'microservices', 'testing', 'performance'],
    duration: 960,
    lessonsCount: 24,
    studentsCount: 980,
    rating: 4.7,
    reviewsCount: 188,
    isPublished: true,
    createdAt: now,
    updatedAt: now,
  },
  {
    id: 'course-rust-cpp-systems',
    title: 'Systems Programming: Rust and C++',
    description: 'Learn memory safety, concurrency, and performance optimization through modern systems programming projects.',
    shortDescription: 'Write safe, fast systems code with Rust and C++.',
    thumbnail: 'https://images.unsplash.com/photo-1516116216624-53e697fedbea?w=1200',
    trailerUrl: 'https://mpb-site.nyc3.digitaloceanspaces.com/vids/homeCourses/Master+Polyglots+-+Rust+%26+C%2B%2B+Intro.mp4',
    instructor: instructors[1],
    level: 'advanced',
    language: 'rust',
    topics: ['memory management', 'concurrency', 'optimization', 'tooling'],
    duration: 1080,
    lessonsCount: 22,
    studentsCount: 620,
    rating: 4.9,
    reviewsCount: 141,
    isPublished: true,
    createdAt: now,
    updatedAt: now,
  },
  {
    id: 'course-node-python-apis',
    title: 'Production APIs with Node.js and Python',
    description: 'Ship backend APIs with authentication, caching, and observability using Node.js and Python.',
    shortDescription: 'Build production-ready APIs in Node.js and Python.',
    thumbnail: 'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=1200',
    trailerUrl: 'https://mpb-site.nyc3.digitaloceanspaces.com/vids/homeCourses/Master+Polyglots+-+NodeJS+%26+Python+Intro.mp4',
    instructor: instructors[0],
    level: 'intermediate',
    language: 'nodejs',
    topics: ['rest apis', 'auth', 'caching', 'monitoring'],
    duration: 840,
    lessonsCount: 20,
    studentsCount: 1310,
    rating: 4.8,
    reviewsCount: 267,
    isPublished: true,
    createdAt: now,
    updatedAt: now,
  },
  {
    id: 'course-angular-react-frontend',
    title: 'Frontend Engineering with Angular and React',
    description: 'Build modern responsive interfaces, manage state, and ship maintainable frontend applications.',
    shortDescription: 'Create scalable frontend apps in Angular and React.',
    thumbnail: 'https://images.unsplash.com/photo-1526379095098-d400fd0bf935?w=1200',
    trailerUrl: 'https://mpb-site.nyc3.digitaloceanspaces.com/vids/homeCourses/Master+Polyglots+-+Angular+%26+React+Intro.mp4',
    instructor: instructors[0],
    level: 'beginner',
    language: 'react',
    topics: ['components', 'state management', 'routing', 'ui architecture'],
    duration: 780,
    lessonsCount: 19,
    studentsCount: 1730,
    rating: 4.9,
    reviewsCount: 355,
    isPublished: true,
    createdAt: now,
    updatedAt: now,
  },
  {
    id: 'course-swift-kotlin-mobile',
    title: 'Mobile App Bootcamp: Swift + Kotlin',
    description: 'Design and build native iOS and Android apps with practical architecture, testing, and deployment workflows.',
    shortDescription: 'Build native mobile apps with Swift and Kotlin.',
    thumbnail: 'https://images.unsplash.com/photo-1511707171634-5f897ff02aa9?w=1200',
    trailerUrl: 'https://mpb-site.nyc3.digitaloceanspaces.com/vids/homeCourses/Master+Polyglots+-+Swift+%26+Kotlin+Intro.mp4',
    instructor: instructors[2],
    level: 'intermediate',
    language: 'kotlin',
    topics: ['mobile ui', 'architecture', 'testing', 'publishing'],
    duration: 900,
    lessonsCount: 21,
    studentsCount: 840,
    rating: 4.7,
    reviewsCount: 194,
    isPublished: true,
    createdAt: now,
    updatedAt: now,
  },
  {
    id: 'course-python-data-automation',
    title: 'Python for Automation and Data Workflows',
    description: 'Automate repetitive tasks and build data pipelines with Python fundamentals, libraries, and tooling.',
    shortDescription: 'Automate workflows and data tasks using Python.',
    thumbnail: 'https://images.unsplash.com/photo-1517694712202-14dd9538aa97?w=1200',
    instructor: instructors[1],
    level: 'beginner',
    language: 'python',
    topics: ['automation', 'data processing', 'scripting', 'debugging'],
    duration: 690,
    lessonsCount: 17,
    studentsCount: 1220,
    rating: 4.6,
    reviewsCount: 229,
    isPublished: true,
    createdAt: now,
    updatedAt: now,
  },
  {
    id: 'course-go-cloud-services',
    title: 'Cloud Services and APIs with Go',
    description: 'Build high-performance cloud services in Go with clean architecture and observability best practices.',
    shortDescription: 'Ship fast cloud services with Go.',
    thumbnail: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1200',
    instructor: instructors[1],
    level: 'advanced',
    language: 'go',
    topics: ['cloud services', 'concurrency', 'deployment', 'observability'],
    duration: 1020,
    lessonsCount: 23,
    studentsCount: 670,
    rating: 4.8,
    reviewsCount: 156,
    isPublished: true,
    createdAt: now,
    updatedAt: now,
  },
  {
    id: 'course-javascript-testing',
    title: 'Testing Modern JavaScript Applications',
    description: 'Learn unit, integration, and end-to-end testing strategies that make teams ship confidently.',
    shortDescription: 'Master JavaScript testing from unit to E2E.',
    thumbnail: 'https://images.unsplash.com/photo-1517180102446-f3ece451e9d8?w=1200',
    instructor: instructors[0],
    level: 'intermediate',
    language: 'javascript',
    topics: ['unit testing', 'integration testing', 'e2e', 'ci'],
    duration: 660,
    lessonsCount: 16,
    studentsCount: 930,
    rating: 4.7,
    reviewsCount: 173,
    isPublished: true,
    createdAt: now,
    updatedAt: now,
  },
  {
    id: 'course-react-typescript',
    title: 'React + TypeScript for Production Teams',
    description: 'Build robust React applications with TypeScript, reusable patterns, and scalable architecture.',
    shortDescription: 'Ship production-grade React apps with TypeScript.',
    thumbnail: 'https://images.unsplash.com/photo-1633356122544-f134324a6cee?w=1200',
    instructor: instructors[0],
    level: 'advanced',
    language: 'react',
    topics: ['typescript', 'component patterns', 'performance', 'tooling'],
    duration: 870,
    lessonsCount: 20,
    studentsCount: 1110,
    rating: 4.9,
    reviewsCount: 244,
    isPublished: true,
    createdAt: now,
    updatedAt: now,
  },
  {
    id: 'course-java-spring',
    title: 'Enterprise APIs with Java Spring',
    description: 'Build secure, maintainable enterprise APIs with Spring Boot and modern engineering practices.',
    shortDescription: 'Build enterprise-grade APIs in Java Spring.',
    thumbnail: 'https://images.unsplash.com/photo-1484417894907-623942c8ee29?w=1200',
    instructor: instructors[1],
    level: 'advanced',
    language: 'java',
    topics: ['spring boot', 'security', 'api design', 'sql'],
    duration: 930,
    lessonsCount: 22,
    studentsCount: 760,
    rating: 4.8,
    reviewsCount: 162,
    isPublished: true,
    createdAt: now,
    updatedAt: now,
  },
  {
    id: 'course-kotlin-android',
    title: 'Android App Development with Kotlin',
    description: 'Build polished Android apps with Kotlin, architecture components, and testing workflows.',
    shortDescription: 'Create Android apps with Kotlin from scratch.',
    thumbnail: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=1200',
    instructor: instructors[2],
    level: 'beginner',
    language: 'kotlin',
    topics: ['android', 'ui', 'state management', 'testing'],
    duration: 810,
    lessonsCount: 19,
    studentsCount: 890,
    rating: 4.6,
    reviewsCount: 139,
    isPublished: true,
    createdAt: now,
    updatedAt: now,
  },
];

const includesValue = (source: string, query?: string): boolean =>
  !query || source.toLowerCase().includes(query.toLowerCase());

const filterMockCourses = (filters: FilterParams): ReadonlyArray<Course> =>
  mockCourses.filter((course) => {
    const searchMatch =
      !filters.search ||
      includesValue(course.title, filters.search) ||
      includesValue(course.shortDescription, filters.search) ||
      course.topics.some((topic) => includesValue(topic, filters.search));
    const levelMatch = !filters.level || course.level === filters.level;
    const languageMatch = !filters.language || course.language === filters.language;
    const topicMatch =
      !filters.topic ||
      course.topics.some((topic) => includesValue(topic, filters.topic));

    return searchMatch && levelMatch && languageMatch && topicMatch;
  });

export const getMockCoursesPage = (filters: FilterParams = {}): Paginated<Course> => {
  const page = Math.max(1, Number(filters.page) || 1);
  const limit = Math.max(1, Number(filters.limit) || 12);
  const filteredCourses = filterMockCourses(filters);

  const startIndex = (page - 1) * limit;
  const endIndex = startIndex + limit;
  const items = filteredCourses.slice(startIndex, endIndex);

  return {
    items,
    meta: {
      page,
      limit,
      total: filteredCourses.length,
      totalPages: Math.max(1, Math.ceil(filteredCourses.length / limit)),
    },
  };
};

export const getMockFeaturedCourses = (): ReadonlyArray<Course> =>
  [...mockCourses]
    .sort((a, b) => b.rating - a.rating)
    .slice(0, 6);

export const getMockCourseById = (courseId: string): Course | undefined =>
  mockCourses.find((course) => course.id === courseId);
